/**
 * Single source of truth for "did a viewport-edit command actually mutate the
 * geometry document?".
 *
 * The seven per-viewport override commands (lock / shade-plot / VPLAYER / VPCLIP
 * set + remove / scale / set-view) each resolve to a command-engine
 * `applyViewport*` function that returns a {@link ViewportMutationResult}
 * carrying a `mutated`
 * flag. That flag is `false` for a no-op: a stale / nonexistent viewport id, the
 * paper-space control viewport (DXF viewportId === 1, never user-modifiable), an
 * idempotent VPLAYER freeze/thaw, or a set-view whose values match the current
 * state.
 *
 * BOTH the geometry-document reducer (`applyLayoutAction`, which produces the
 * next document) AND the shell-audit emitter (`reduceViewportEditShellAudit`,
 * which gates `pushAuditEvent` on a genuine mutation) call this one dispatcher so
 * the "is it a real mutation?" decision is computed in exactly one place. This
 * mirrors the App.tsx zoom/pan FIX #269 idiom: a no-op view-op must emit NO false
 * "success" audit / telemetry / transcript row (cleanup Q6c #269).
 */

import {
  applyViewportClipRemove,
  applyViewportClipSet,
  applyViewportClipSetPolygon,
  applyViewportSetLock,
  applyViewportSetScale,
  applyViewportSetShadePlot,
  applyViewportSetView,
  applyViewportVplayer,
  type ViewportMutationResult,
} from "@dxf-ai-editor/command-engine";
import type {
  DynamicBlockCommand,
  EditorCommand,
} from "@dxf-ai-editor/contracts";
import type { DrawingDocument } from "@dxf-ai-editor/drawing-model";

/**
 * Resolve a viewport-edit command to its {@link ViewportMutationResult} against
 * `doc`, or `null` when `command` is not one of the seven viewport-edit
 * commands.
 *
 * Pure: it never mutates `doc` (the underlying `applyViewport*` helpers clone the
 * target entity), so it is safe to call once on the shell side (to decide whether
 * to emit an audit) and again on the geometry side (to produce the next
 * document) — both observe the same deterministic result for the same inputs.
 */
export function computeViewportEditMutation(
  doc: DrawingDocument,
  command: EditorCommand | DynamicBlockCommand,
): ViewportMutationResult | null {
  switch (command.type) {
    case "geometry/viewport-set-lock":
      return applyViewportSetLock(doc, command.viewportId, command.locked);
    case "geometry/viewport-set-shadeplot":
      return applyViewportSetShadePlot(
        doc,
        command.viewportId,
        command.shadePlotMode,
      );
    case "geometry/viewport-vplayer":
      return applyViewportVplayer(
        doc,
        command.viewportId,
        command.action,
        command.layerName,
        // Undo-restore of the pre-mutation native 331 carrier (preserve-source);
        // present only on the computed inverse. `undefined` (forward dispatch) =
        // normal freeze/thaw incl. the positive thaw-drop; a value/null triggers
        // the verbatim restore of the exact prior handle list (@codex #486).
        command.restoreFrozenLayerHandles !== undefined
          ? { restoreFrozenLayerHandles: command.restoreFrozenLayerHandles }
          : undefined,
      );
    case "geometry/viewport-clip-set":
      return applyViewportClipSet(doc, command.viewportId, command.bounds);
    case "geometry/viewport-clip-set-polygon":
      return applyViewportClipSetPolygon(doc, command.viewportId, {
        boundary: command.boundary,
        boundaryEntityId: command.boundaryEntityId,
      });
    case "geometry/viewport-clip-remove":
      return applyViewportClipRemove(doc, command.viewportId);
    case "geometry/viewport-set-scale":
      return applyViewportSetScale(
        doc,
        command.viewportId,
        command.scaleNum,
        command.scaleDenom,
      );
    case "geometry/viewport-set-view":
      return applyViewportSetView(doc, command.viewportId, {
        viewCenter: command.viewCenter,
        viewHeight: command.viewHeight,
        // Undo-restore of the pre-mutation displayed scale (preserve-source);
        // present only on the computed inverse. The scale has TWO independent
        // carriers (typed field + VP_OVERRIDES XDATA) that can diverge, so BOTH
        // snapshots are threaded and each is restored verbatim — never inferring
        // one from the other (@codex #419) — instead of the forward's lossy
        // re-derive.
        restoreVpOverrideScale: command.restoreVpOverrideScale,
        restoreXdataScale: command.restoreXdataScale,
      });
    default:
      return null;
  }
}
